import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getIncidentDetails,
  updateIncidentDetails,
  createNewIncident,
} from '../store/slices/incidentSlice';
import { INCIDENT_FLOW } from '../config/incidentFlowConfig';
import Part1 from './Part1';
import ImmediateAction from './ImmediateAction';
import HODDetails from './HODDetails';
import ActionPlan from './ActionPlan';
import UserComment from './UserComment';
import HODSelection from './HODSelection';
import { setAlert } from '../store/slices/alertSlice';
import { downloadIncidentExport } from '../services/api';

const emptyHODDetails = {
  hodComments: '',
  attachments: [],
  rootCause: {
    human: '',
    material: '',
    environment: '',
    policies: '',
  },
};

const QUALITY_ROLES = ['Admin', 'Quality Manager', 'Quality Staff'];
const INCIDENT_CATEGORY_FIELDS = [
  'needle&sharps',
  'intravenouscomplications',
  'occupationalhealthandsafety',
  'safetyvariance',
  'radiationsafetyvariance',
  'patientfall',
  'behaviorvariance',
  'proceduralvariance',
  'securityvariance',
  'narcoticmedicationvariance',
  'clinicalvariance',
];

const createEmptyHODDetails = () => ({
  ...emptyHODDetails,
  rootCause: { ...emptyHODDetails.rootCause },
});

const createEmptyFormData = (user) => ({
  Part1: {
    name: user?.username || user?.name || user?.fullName || '',
  },
  ImmediateAction: {},
  UserComment: { comment: '', attachments: [] },
  HODAssignment: {},
  HODDetails: createEmptyHODDetails(),
  ActionPlan: [],
});

const getValueId = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value._id || value.id || '';
};

const uniqueIds = (ids) => [...new Set(ids.map(getValueId).filter(Boolean))];

const getUserName = (value, fallback = '') => {
  if (!value || typeof value === 'string') return fallback;
  return value.username || value.name || value.fullName || fallback;
};

const sanitizeActionPlanRows = (rows = []) =>
  rows.filter((row) =>
    row.areaForImprovement || row.actionPlan || row.responsibility || row.targetDate
  );

const getTime = (value) => {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const getFilenameFromDisposition = (disposition, fallback) => {
  const match = disposition?.match(/filename="?([^"]+)"?/i);
  return match?.[1] || fallback;
};

const IncidentForm = ({ incident, closeModal, refreshDashboardData }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);

  const role = user?.role;
  const currentUserId = getValueId(user);
  const incidentId = incident?._id;
  const isEditMode = Boolean(incidentId);

  const [incidentDet, setIncident] = useState(null);
  const [showResendModal, setShowResendModal] = useState(false);
  const [resendData, setResendData] = useState({
    type: '',
    targetId: '',
    parentId: '',
  });
  const [remarkText, setRemarkText] = useState('');

  const [formData, setFormData] = useState(() => createEmptyFormData(user));
  const [editingHodDetails, setEditingHodDetails] = useState(false);
  const [hodEditTargetId, setHodEditTargetId] = useState('');
  const [editingActionPlan, setEditingActionPlan] = useState(false);

  const [expandedSections, setExpandedSections] = useState({
    Part1: true,
    ImmediateAction: false,
    UserComment: false,
    HODSelection: false,
    HODDetails: false,
    ActionPlan: false,
  });

  const status = incidentDet?.Part1?.currentStatus;
  const isQualityRole = QUALITY_ROLES.includes(role);

  const getPermission = (section) => {
    const flow = INCIDENT_FLOW[section];

    if (!flow) {
      return { canShow: false, canEdit: false };
    }

    return {
      canShow: flow.show({ role, status }),
      canEdit: flow.edit({ role, status }),
    };
  };

  const part1 = getPermission('Part1');
  const immediate = getPermission('ImmediateAction');
  const userComment = getPermission('UserComment');
  const hodSelection = getPermission('HODSelection');
  const hodDetails = getPermission('HODDetails');
  const actionPlanPermission = getPermission('ActionPlan');
  const closeSection = getPermission('Close');

  useEffect(() => {
    if (!incidentId) {
      setIncident(null);
      setFormData(createEmptyFormData(user));
      return;
    }

    dispatch(getIncidentDetails(incidentId)).then((res) => {
      setIncident(res);
      setFormData({
        Part1: {
          ...(res.Part1 || {}),
          name: getUserName(res.Part1?.createdBy, res.Part1?.name || ''),
        },
        ImmediateAction: {
          ...(res.ImmediateAction || {}),
          userIds: uniqueIds(res.ImmediateAction?.userIds || []),
          userObjects: Array.isArray(res.ImmediateAction?.userIds)
            ? res.ImmediateAction.userIds.filter((item) => typeof item === 'object')
            : [],
        },
        UserComment: {
          comment: '',
          attachments: [],
        },
        HODAssignment: {
          ...(res.HODAssignment || {}),
          hodIds: uniqueIds(res.HODAssignment?.hodIds || []),
          hodObjects: Array.isArray(res.HODAssignment?.hodIds)
            ? res.HODAssignment.hodIds.filter((item) => typeof item === 'object')
            : [],
          primaryHodId: getValueId(res.HODAssignment?.primaryHodId),
        },
        HODDetails: createEmptyHODDetails(),
        ActionPlan: res.ActionPlan || [],
      });
    });
  }, [dispatch, incidentId, user]);

  const handleFormDataChange = (section, data) => {
    setFormData((prev) => ({ ...prev, [section]: data }));
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleResend = ({ type, targetId, parentId }) => {
    setResendData({ type, targetId, parentId });
    setShowResendModal(true);
  };

  const selectedUserIds = uniqueIds(formData.ImmediateAction?.userIds || []);
  const selectedHodIds = uniqueIds(formData.HODAssignment?.hodIds || []);
  const userCommentHistory = incidentDet?.UserComment || [];
  const hodCommentHistory = incidentDet?.HODDetails || [];
  const hasUserRepliedAfterLatestResend = (userId) => {
    const latestResend = userCommentHistory
      .filter((comment) => comment.status === 'Resent' && getValueId(comment.targetUserId || comment.userId) === userId)
      .reduce((latest, comment) => Math.max(latest, getTime(comment.createdDate)), 0);

    const latestReply = userCommentHistory
      .filter((comment) => comment.status !== 'Resent' && getValueId(comment.userId || comment.createdBy) === userId)
      .reduce((latest, comment) => Math.max(latest, getTime(comment.createdDate)), 0);

    return latestReply > latestResend;
  };
  const getLatestPendingUserResend = (userId) => {
    const latestResend = userCommentHistory
      .filter((comment) => comment.status === 'Resent' && getValueId(comment.targetUserId || comment.userId) === userId)
      .sort((a, b) => getTime(b.createdDate) - getTime(a.createdDate))[0];

    if (!latestResend) return null;

    const latestReplyTime = userCommentHistory
      .filter((comment) => comment.status !== 'Resent' && getValueId(comment.userId || comment.createdBy) === userId)
      .reduce((latest, comment) => Math.max(latest, getTime(comment.createdDate)), 0);

    return latestReplyTime > getTime(latestResend.createdDate) ? null : latestResend;
  };
  const hasHodSubmittedAfterLatestResend = (hodId) => {
    const latestResend = hodCommentHistory
      .filter((comment) => comment.status === 'Resent' && getValueId(comment.targetHodId || comment.hodId) === hodId)
      .reduce((latest, comment) => Math.max(latest, getTime(comment.createdDate)), 0);

    const latestReply = hodCommentHistory
      .filter((comment) => comment.status !== 'Resent' && getValueId(comment.hodId || comment.createdBy) === hodId)
      .reduce((latest, comment) => Math.max(latest, getTime(comment.createdDate)), 0);

    return latestReply > latestResend;
  };
  const getLatestPendingHodResend = (hodId) => {
    const latestResend = hodCommentHistory
      .filter((comment) => comment.status === 'Resent' && getValueId(comment.targetHodId || comment.hodId) === hodId)
      .sort((a, b) => getTime(b.createdDate) - getTime(a.createdDate))[0];

    if (!latestResend) return null;

    const latestReplyTime = hodCommentHistory
      .filter((comment) => comment.status !== 'Resent' && getValueId(comment.hodId || comment.createdBy) === hodId)
      .reduce((latest, comment) => Math.max(latest, getTime(comment.createdDate)), 0);

    return latestReplyTime > getTime(latestResend.createdDate) ? null : latestResend;
  };
  const isAssignedUser = selectedUserIds.includes(currentUserId);
  const hasUsers = selectedUserIds.length > 0;
  const allSelectedUsersReplied =
    !hasUsers || selectedUserIds.every((id) => hasUserRepliedAfterLatestResend(id));
  const allSelectedHodsSubmitted =
    selectedHodIds.length > 0 && selectedHodIds.every((id) => hasHodSubmittedAfterLatestResend(id));
  const isPrimaryHod = currentUserId === getValueId(formData.HODAssignment?.primaryHodId);
  const rootCause = formData.HODDetails?.rootCause || {};
  const hasRootCause =
    Boolean(rootCause.human?.trim()) &&
    Boolean(rootCause.material?.trim()) &&
    Boolean(rootCause.environment?.trim()) &&
    Boolean(rootCause.policies?.trim());
  const canShowHODSelection = hodSelection.canShow && allSelectedUsersReplied;
  const canShowHODDetails =
    hodDetails.canShow && (isQualityRole || selectedHodIds.includes(currentUserId));
  const canShowActionPlan = actionPlanPermission.canShow && allSelectedHodsSubmitted;
  const canShowUserComment =
    isQualityRole ||
    (role === 'Staff' && isAssignedUser) ||
    (role === 'Supervisor/HOD' && selectedHodIds.includes(currentUserId));
  const canResendComments =
    isQualityRole && ['Sent for User Comment', 'Sent for HOD Review'].includes(status);
  const canEditSubmittedContent =
    ['Admin', 'Quality Manager'].includes(role) &&
    ['HOD Comment Submitted', 'Final Review', 'Closed'].includes(status);
  const canCurrentUserSubmitComment =
    userComment.canEdit &&
    isAssignedUser &&
    !hasUserRepliedAfterLatestResend(currentUserId);
  const canCurrentHodSubmit =
    hodDetails.canEdit &&
    role === 'Supervisor/HOD' &&
    selectedHodIds.includes(currentUserId) &&
    !hasHodSubmittedAfterLatestResend(currentUserId);
  const canEditHodDetails =
    canCurrentHodSubmit || (canEditSubmittedContent && editingHodDetails);
  const canEditActionPlan =
    (actionPlanPermission.canEdit && status === 'HOD Comment Submitted') ||
    (canEditSubmittedContent && editingActionPlan);

  const showValidationError = (message) => {
    dispatch(setAlert({ type: 'danger', message }));
  };

  const getValidationMessage = (section) => {
    if (section === 'Part1') {
      const part = formData.Part1 || {};
      if (!part.department) return 'Department is required.';
      if (!part.name?.trim()) return 'Name is required.';
      if (!part.personInvolved?.length) return 'Select at least one person involved.';
      if (!INCIDENT_CATEGORY_FIELDS.some((field) => part[field]) && !part.othersSpecify?.trim()) {
        return 'Select an incident category or enter other details.';
      }
      if (!part.factualDescription?.trim()) return 'Factual description is required.';
      if (!part.immediateAction?.trim()) return 'Immediate action is required.';
      if (part.medicationError && !part.drugName?.trim()) return 'Drug name is required for medication error.';
      if (part.medicationError && !part.dose?.trim()) return 'Dose is required for medication error.';
    }

    if (section === 'ImmediateAction') {
      const details = formData.ImmediateAction || {};
      if (!details.investigationFindings?.trim()) return 'Investigation details are required.';
      if (!details.department) return 'Investigation department is required.';
      if (!details.priority) return 'Priority is required.';
    }

    if (section === 'UserComment') {
      const comment = formData.UserComment || {};
      if (!comment.comment?.trim()) return 'Comment is required.';
    }

    if (section === 'HODSelection') {
      const assignment = formData.HODAssignment || {};
      if (!assignment.hodIds?.length) return 'Select at least one HOD.';
      if (!assignment.primaryHodId) return 'Select a primary HOD.';
    }

    if (section === 'HODDetails') {
      if (!formData.HODDetails?.hodComments?.trim()) return 'HOD comment is required.';
      if (isPrimaryHod && !hasRootCause) return 'Primary HOD must enter all root cause fields.';
    }

    if (section === 'ActionPlan') {
      const rows = (formData.ActionPlan || []).filter((row) =>
        row.areaForImprovement || row.actionPlan || row.responsibility || row.targetDate
      );

      if (!rows.length) return 'Add at least one action plan row.';

      const incomplete = rows.some((row) =>
        !row.areaForImprovement?.trim() ||
        !row.actionPlan?.trim() ||
        !row.responsibility?.trim() ||
        !row.targetDate
      );

      if (incomplete) return 'Complete all fields in each action plan row.';
    }

    return '';
  };

  const expandAfterStatus = (nextStatus) => {
    const sectionByStatus = {
      'OVR Submitted': 'ImmediateAction',
      'Sent for User Comment': 'UserComment',
      'Investigation Completed': 'HODSelection',
      'Sent for HOD Review': 'HODDetails',
      'HOD Comment Submitted': 'ActionPlan',
      'Final Review': 'ActionPlan',
    };
    const nextSection = sectionByStatus[nextStatus];

    if (nextSection) {
      setExpandedSections((prev) => ({ ...prev, [nextSection]: true }));
    }
  };

  const submitResend = () => {
    if (!remarkText.trim()) {
      showValidationError('Enter remark.');
      return;
    }

    const payload = {
      section: resendData.type === 'user' ? 'UserComment' : 'HODDetails',
      newStatus: 'Resent',
      parentId: resendData.parentId,
      remarkFromQuality: remarkText,
    };

    if (resendData.type === 'user') {
      payload.UserComment = {
        targetUserId: resendData.targetId,
      };
    } else {
      payload.HODDetails = {
        targetHodId: resendData.targetId,
      };
    }

    dispatch(updateIncidentDetails(incidentId, payload)).then(() => {
      dispatch(getIncidentDetails(incidentId)).then(setIncident);
      setShowResendModal(false);
      setRemarkText('');
      refreshDashboardData?.();
      dispatch(setAlert({ type: 'success', message: 'Comment resent successfully' }));
    });
  };

  const executeSubmit = (nextStatus, section, { validate = true } = {}) => {
    if (validate) {
      const validationMessage = getValidationMessage(section);

      if (validationMessage) {
        showValidationError(validationMessage);
        return;
      }
    }

    let effectiveStatus = nextStatus;
    let payload = {
      section,
      newStatus: effectiveStatus,
      modifiedBy: currentUserId,
      modifiedByName: getUserName(user),
    };

    if (section === 'Part1') {
      payload.Part1 = formData.Part1;
    }

    if (section === 'ImmediateAction') {
      effectiveStatus = hasUsers ? 'Sent for User Comment' : 'Investigation Completed';
      payload = {
        ...payload,
        newStatus: effectiveStatus,
        ImmediateAction: formData.ImmediateAction,
      };
    }

    if (section === 'UserComment') {
      const pendingResend = getLatestPendingUserResend(currentUserId);
      payload = {
        ...payload,
        parentId: pendingResend?._id || null,
        newStatus: selectedUserIds.every((id) =>
          id === currentUserId || hasUserRepliedAfterLatestResend(id)
        )
          ? 'Investigation Completed'
          : 'Sent for User Comment',
        UserComment: formData.UserComment,
      };
    }

    if (section === 'HODSelection') {
      payload.HODAssignment = formData.HODAssignment;
    }

    if (section === 'HODDetails') {
      const pendingResend = getLatestPendingHodResend(currentUserId);
      payload = {
        ...payload,
        parentId: pendingResend?._id || null,
        newStatus: selectedHodIds.every((id) =>
          id === currentUserId || hasHodSubmittedAfterLatestResend(id)
        )
          ? 'HOD Comment Submitted'
          : 'Sent for HOD Review',
        HODDetails: formData.HODDetails,
      };
    }

    if (section === 'ActionPlan') {
      payload.ActionPlan = sanitizeActionPlanRows(formData.ActionPlan);
    }

    if (section === 'Incident') {
      payload = {
        section: 'Incident',
        newStatus: effectiveStatus,
        modifiedBy: currentUserId,
        modifiedByName: getUserName(user),
      };
    }

    if (isEditMode) {
      dispatch(updateIncidentDetails(incidentId, payload))
        .then(() => dispatch(getIncidentDetails(incidentId)))
        .then((res) => {
          if (res) {
            setIncident(res);
            expandAfterStatus(res.Part1?.currentStatus || payload.newStatus);
          }
          refreshDashboardData?.();
          setEditingHodDetails(false);
          setHodEditTargetId('');
          setEditingActionPlan(false);
          dispatch(setAlert({ type: 'success', message: 'Updated successfully' }));
          if (section === 'Incident') {
            closeModal?.();
          }
        })
        .catch(() => {
          dispatch(setAlert({ type: 'danger', message: 'Update failed' }));
        });
    } else {
      dispatch(createNewIncident(payload)).then(() => {
        refreshDashboardData?.();
        setFormData(createEmptyFormData(user));
        setIncident(null);
        setExpandedSections({
          Part1: true,
          ImmediateAction: false,
          UserComment: false,
          HODSelection: false,
          HODDetails: false,
          ActionPlan: false,
        });
        closeModal?.();
        dispatch(setAlert({ type: 'success', message: 'Created successfully' }));
      });
    }
  };

  const handleSubmit = (nextStatus, section) => {
    const validationMessage = getValidationMessage(section);

    if (validationMessage) {
      showValidationError(validationMessage);
      return;
    }

    const isClose = section === 'Incident' && nextStatus === 'Closed';
    const message = isClose
      ? 'Are you sure you want to close this incident?'
      : 'Are you sure you want to submit this section?';

    if (window.confirm(message)) {
      executeSubmit(nextStatus, section, { validate: false });
    }
  };

  const handleEditOnlyUpdate = (section) => {
    const payload = {
      section,
      newStatus: status,
      modifiedBy: currentUserId,
      modifiedByName: getUserName(user),
      editOnly: true,
    };

    if (section === 'HODDetails') {
      payload.HODDetails = formData.HODDetails;
      payload.targetCommentId = hodEditTargetId;
    }

    if (section === 'ActionPlan') {
      payload.ActionPlan = sanitizeActionPlanRows(formData.ActionPlan);
    }

    dispatch(updateIncidentDetails(incidentId, payload))
      .then(() => dispatch(getIncidentDetails(incidentId)))
      .then((res) => {
        if (res) setIncident(res);
        setEditingHodDetails(false);
        setHodEditTargetId('');
        setEditingActionPlan(false);
        refreshDashboardData?.();
        dispatch(setAlert({ type: 'success', message: 'Changes updated successfully' }));
      })
      .catch(() => {
        dispatch(setAlert({ type: 'danger', message: 'Update failed' }));
      });
  };

  const startHodDetailsEdit = () => {
    const latestSubmitted = [...hodCommentHistory]
      .filter((comment) => comment.status !== 'Resent')
      .sort((a, b) => getTime(b.createdDate) - getTime(a.createdDate))[0];

    setFormData((prev) => ({
      ...prev,
      HODDetails: {
        hodComments: latestSubmitted?.hodComments || '',
        attachments: [],
        rootCause: {
          ...emptyHODDetails.rootCause,
          ...(latestSubmitted?.rootCause || {}),
        },
      },
    }));
    setHodEditTargetId(getValueId(latestSubmitted?._id));
    setEditingHodDetails(true);
  };

  const handleActionPlanDraft = () => {
    executeSubmit(status || 'HOD Comment Submitted', 'ActionPlan', { validate: false });
  };

  const handleExport = async (type) => {
    try {
      const response = await downloadIncidentExport(incidentId, type, { maskCreatedBy: true });
      const extension = type === 'excel' ? 'xlsx' : 'pdf';
      const filename = getFilenameFromDisposition(
        response.headers?.['content-disposition'],
        `Incident_${formData.Part1?.refNo || incidentId}.${extension}`
      );
      const blob = new Blob([response.data], {
        type: response.headers?.['content-type'] || 'application/octet-stream',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      dispatch(setAlert({
        type: 'danger',
        message: `${type === 'excel' ? 'Excel' : 'PDF'} export failed`,
      }));
    }
  };

  return (
    <div>
      <div className="accordion" id="incidentAccordion">
        <div className="accordion-item">
          <h2 className="accordion-header">
            <button
              className={`accordion-button ${!expandedSections.Part1 ? 'collapsed' : ''}`}
              type="button"
              onClick={() => toggleSection('Part1')}
            >
              Incident Details
            </button>
          </h2>

          <div className={`accordion-collapse collapse ${expandedSections.Part1 ? 'show' : ''}`}>
            {part1.canShow && (
              <div className="accordion-body">
                <Part1
                  data={formData.Part1}
                  disabled={!part1.canEdit}
                  currentUser={user}
                  maskName={!isQualityRole}
                  onChange={(d) => handleFormDataChange('Part1', d)}
                />

                {part1.canEdit && (
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-success btn-sm me-2"
                      onClick={() => handleSubmit('OVR Submitted', 'Part1')}
                    >
                      Submit
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="accordion-item">
          <h2 className="accordion-header">
            <button
              className={`accordion-button ${!expandedSections.ImmediateAction ? 'collapsed' : ''}`}
              type="button"
              onClick={() => toggleSection('ImmediateAction')}
            >
              Investigation Details
            </button>
          </h2>

          <div className={`accordion-collapse collapse ${expandedSections.ImmediateAction ? 'show' : ''}`}>
            {immediate.canShow && (
              <div className="accordion-body">
                <ImmediateAction
                  data={formData.ImmediateAction}
                  disabled={!immediate.canEdit}
                  onChange={(d) => handleFormDataChange('ImmediateAction', d)}
                />

                {immediate.canEdit && (
                  <button
                    className="btn btn-success btn-sm me-2"
                    onClick={() => handleSubmit(
                      hasUsers ? 'Sent for User Comment' : 'Investigation Completed',
                      'ImmediateAction'
                    )}
                  >
                    {hasUsers ? 'Send for User Comment' : 'Complete Investigation'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {canShowUserComment && userComment.canShow && (
          <div className="accordion-item">
            <h2 className="accordion-header">
              <button
                className={`accordion-button ${!expandedSections.UserComment ? 'collapsed' : ''}`}
                type="button"
                onClick={() => toggleSection('UserComment')}
              >
                User Comments
              </button>
            </h2>

            <div className={`accordion-collapse collapse ${expandedSections.UserComment ? 'show' : ''}`}>
              <div className="accordion-body">
                <UserComment
                  data={formData.UserComment}
                  history={incidentDet?.UserComment || []}
                  onChange={(d) => handleFormDataChange('UserComment', d)}
                  currentUser={currentUserId}
                  role={role}
                  assignedUsers={formData.ImmediateAction?.userObjects || []}
                  disabled={!canCurrentUserSubmitComment}
                  onResend={canResendComments ? handleResend : null}
                />

                {canCurrentUserSubmitComment && (
                <div className="mt-3 d-flex gap-2">
                  <button
                    className="btn btn-success btn-sm"
                    onClick={() => handleSubmit('Investigation Completed', 'UserComment')}
                  >
                    Submit Comment
                  </button>
                </div>
                )}
              </div>
            </div>
          </div>
        )}

        {canShowHODSelection && (
          <div className="accordion-item">
            <h2 className="accordion-header">
              <button
                className={`accordion-button ${!expandedSections.HODSelection ? 'collapsed' : ''}`}
                type="button"
                onClick={() => toggleSection('HODSelection')}
              >
                HOD Assignment
              </button>
            </h2>

            <div className={`accordion-collapse collapse ${expandedSections.HODSelection ? 'show' : ''}`}>
              <div className="accordion-body">
                <HODSelection
                  data={formData.HODAssignment}
                  disabled={!hodSelection.canEdit}
                  onChange={(d) => handleFormDataChange('HODAssignment', d)}
                />

                {hodSelection.canEdit && (
                <div className="mt-3 d-flex gap-2">
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={!formData.HODAssignment?.hodIds?.length}
                    onClick={() => handleSubmit('Sent for HOD Review', 'HODSelection')}
                  >
                    Send to HOD
                  </button>
                </div>
                )}
              </div>
            </div>
          </div>
        )}

        {canShowHODDetails && (
          <div className="accordion-item">
            <h2 className="accordion-header">
              <button
                className={`accordion-button ${!expandedSections.HODDetails ? 'collapsed' : ''}`}
                type="button"
                onClick={() => toggleSection('HODDetails')}
              >
                HOD Investigation
              </button>
            </h2>

            <div className={`accordion-collapse collapse ${expandedSections.HODDetails ? 'show' : ''}`}>
              <div className="accordion-body">
                <HODDetails
                  data={formData.HODDetails}
                  history={incidentDet?.HODDetails || []}
                  onChange={(d) => handleFormDataChange('HODDetails', d)}
                  currentUser={currentUserId}
                  role={role}
                  primaryHodId={incidentDet?.HODAssignment?.primaryHodId}
                  assignedHods={formData.HODAssignment?.hodObjects || []}
                  disabled={!canEditHodDetails}
                  showRootCauseEditor={editingHodDetails}
                  onResend={canResendComments ? handleResend : null}
                />

                <div className="mt-3 d-flex gap-2">
                  {canEditSubmittedContent && !editingHodDetails && (
                    <button
                      className="btn btn-outline-primary btn-sm"
                      type="button"
                      onClick={startHodDetailsEdit}
                    >
                      Edit HOD Details
                    </button>
                  )}
                  {canEditSubmittedContent && editingHodDetails && (
                    <>
                      <button
                        className="btn btn-primary btn-sm"
                        type="button"
                        onClick={() => handleEditOnlyUpdate('HODDetails')}
                      >
                        Update HOD Details
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        type="button"
                        onClick={() => {
                          setEditingHodDetails(false);
                          setHodEditTargetId('');
                        }}
                      >
                        Cancel
                      </button>
                    </>
                  )}
                  {canCurrentHodSubmit && (
                    <button
                      className="btn btn-success btn-sm"
                      disabled={
                        !formData.HODDetails?.hodComments ||
                        (isPrimaryHod && !hasRootCause)
                      }
                      onClick={() => handleSubmit('HOD Comment Submitted', 'HODDetails')}
                    >
                      Submit HOD Comment
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {showResendModal && (
          <>
          <div className="modal fade show d-block resend-modal" tabIndex="-1" style={{ zIndex: 1060 }}>
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Resend Comment</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowResendModal(false)}
                  ></button>
                </div>

                <div className="modal-body">
                  <label className="form-label">Remark</label>
                  <textarea
                    value={remarkText}
                    onChange={(e) => setRemarkText(e.target.value)}
                    className="form-control"
                    placeholder="Enter remark..."
                    rows={4}
                  />
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowResendModal(false)}
                  >
                    Cancel
                  </button>

                  <button
                    className="btn btn-primary"
                    onClick={submitResend}
                    disabled={!remarkText.trim()}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ zIndex: 1055 }}></div>
          </>
        )}

        {canShowActionPlan && (
          <div className="accordion-item">
            <h2 className="accordion-header">
              <button
                className={`accordion-button ${!expandedSections.ActionPlan ? 'collapsed' : ''}`}
                type="button"
                onClick={() => toggleSection('ActionPlan')}
              >
                Action Plan
              </button>
            </h2>

            <div className={`accordion-collapse collapse ${expandedSections.ActionPlan ? 'show' : ''}`}>
              <div className="accordion-body">
                <ActionPlan
                  data={formData.ActionPlan}
                  onChange={(d) => handleFormDataChange('ActionPlan', d)}
                  isActionPlanLineButtons={canEditActionPlan}
                />

                <div className="mt-3 d-flex gap-2">
                  {actionPlanPermission.canEdit && status === 'HOD Comment Submitted' && (
                    <button
                      className="btn btn-secondary btn-sm"
                      type="button"
                      onClick={handleActionPlanDraft}
                    >
                      Save Draft
                    </button>
                  )}
                  {actionPlanPermission.canEdit && status === 'HOD Comment Submitted' && (
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleSubmit('Final Review', 'ActionPlan')}
                    >
                      Submit Action Plan
                    </button>
                  )}
                  {canEditSubmittedContent && !editingActionPlan && (
                    <button
                      className="btn btn-outline-primary btn-sm"
                      type="button"
                      onClick={() => setEditingActionPlan(true)}
                    >
                      Edit Action Plan
                    </button>
                  )}
                  {canEditSubmittedContent && editingActionPlan && (
                    <>
                      <button
                        className="btn btn-primary btn-sm"
                        type="button"
                        onClick={() => handleEditOnlyUpdate('ActionPlan')}
                      >
                        Update Action Plan
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        type="button"
                        onClick={() => setEditingActionPlan(false)}
                      >
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {closeSection.canShow && closeSection.canEdit && (
          <div className="mt-4 d-flex justify-content-end">
            <button
              className="btn btn-danger"
              disabled={status === 'Closed'}
              onClick={() => handleSubmit('Closed', 'Incident')}
            >
              Close Incident
            </button>
          </div>
        )}

        {isQualityRole && ['Final Review', 'Closed'].includes(status) && (
          <div className="mt-3 d-flex justify-content-end gap-2">
            <button
              className="btn btn-outline-success btn-sm"
              onClick={() => handleExport('excel')}
            >
              Download Excel
            </button>

            <button
              className="btn btn-outline-danger btn-sm"
              onClick={() => handleExport('pdf')}
            >
              Download PDF
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncidentForm;
